import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Plus, Edit2, Trash2, Copy, GripVertical, Upload, FileSpreadsheet, Download, Save, X, ChevronDown, ChevronUp, ChevronRight, Image, ArrowUp, ArrowDown, Move, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Product Item Component
const SortableProductItem = ({ product, onEdit, onDuplicate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="bg-[#0f0f15] border-white/5">
      <CardContent className="p-3">
        <div className="flex gap-3">
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="flex items-center cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60"
          >
            <GripVertical className="w-4 h-4" />
          </div>
          {product.images?.[0] ? (
            <img src={product.images[0]} alt="" className="w-16 h-16 rounded object-cover" />
          ) : (
            <div className="w-16 h-16 rounded bg-white/5 flex items-center justify-center">
              <Image className="w-6 h-6 text-white/20" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <h4 className="font-medium text-white truncate">{product.name}</h4>
              {!product.is_active && (
                <Badge className="bg-red-500/20 text-red-400 text-xs ml-1">Inativo</Badge>
              )}
              {product.show_in_delivery === false && (
                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs ml-1" data-testid={`product-pdv-only-${product.id}`}>Só PDV</Badge>
              )}
            </div>
            <p className="text-[#ff6b00] font-bold">
              R$ {product.price.toFixed(2)}
              {product.promo_price && (
                <span className="text-white/40 line-through text-sm ml-2">
                  R$ {product.promo_price.toFixed(2)}
                </span>
              )}
            </p>
            <p className="text-sm text-white/50">Estoque: {product.stock_quantity}</p>
          </div>
        </div>
        <div className="flex gap-1 mt-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(product)}
            className="flex-1 text-white/60 h-8"
            data-testid={`product-edit-${product.id}`}
          >
            <Edit2 className="w-3 h-3 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDuplicate(product.id)}
            className="text-white/60 h-8"
          >
            <Copy className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(product.id)}
            className="text-red-400 h-8"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable Category Component
const SortableCategoryItem = ({ category, index, totalCategories, children, onEdit, onDelete, onMoveUp, onMoveDown, isCollapsed, onToggleCollapse, productCount }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="card-adegga border-0 rounded-lg overflow-hidden">
      <div 
        className="px-4 py-3 flex items-center gap-3 bg-[#13131f] cursor-pointer hover:bg-[#1a1a2e] transition-colors"
        onClick={onToggleCollapse}
      >
        {/* Collapse/Expand button */}
        <button 
          className="text-white/60 hover:text-white transition-colors"
          onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
        
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-5 h-5" />
        </div>
        
        {/* Arrow buttons */}
        <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="ghost"
            disabled={index === 0}
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            className="h-5 w-5 p-0 text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20"
          >
            <ArrowUp className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={index === totalCategories - 1}
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            className="h-5 w-5 p-0 text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20"
          >
            <ArrowDown className="w-3 h-3" />
          </Button>
        </div>
        
        <div className="flex-1 flex items-center gap-3">
          <span className="font-bold text-white">{category.name}</span>
          <Badge className="bg-white/10 text-white/60">{productCount} produtos</Badge>
          {!category.is_visible && (
            <Badge className="bg-yellow-500/20 text-yellow-400">Só PDV</Badge>
          )}
        </div>
        
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="text-white/60"
            data-testid={`category-edit-${category.id}`}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {!isCollapsed && children}
    </div>
  );
};

const Menu = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cards');
  
  // Collapsed categories state
  const [collapsedCategories, setCollapsedCategories] = useState({});
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Form states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', order: 0, is_visible: true });
  const [productForm, setProductForm] = useState({
    name: '', description: '', category_id: '', price: '', promo_price: '',
    sku: '', tags: '', stock_quantity: 100, stock_control: true, is_active: true, show_in_delivery: true,
    variants: [], addons: [], images: [], option_groups: []
  });
  
  // Batch entry
  const [batchRows, setBatchRows] = useState([
    { category: '', name: '', price: '', stock: '' }
  ]);

  // DnD sensors - must be at top level of component
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // CSV Import
  const [csvContent, setCsvContent] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`)
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (e) {
      toast.error('Erro ao carregar cardápio');
    } finally {
      setLoading(false);
    }
  };

  // Toggle category collapse
  const toggleCategoryCollapse = (categoryId) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Collapse/Expand all categories
  const collapseAllCategories = () => {
    const allCollapsed = {};
    categories.forEach(cat => {
      allCollapsed[cat.id] = true;
    });
    setCollapsedCategories(allCollapsed);
  };

  const expandAllCategories = () => {
    setCollapsedCategories({});
  };

  // Category handlers
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm);
        toast.success('Categoria atualizada');
      } else {
        await axios.post(`${API}/categories`, categoryForm);
        toast.success('Categoria criada');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', order: 0, is_visible: true });
      fetchData();
    } catch (e) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Excluir categoria?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Categoria removida');
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover categoria');
    }
  };

  // Reorder categories (for arrow buttons)
  const handleMoveCategory = async (index, direction) => {
    const newCategories = [...categories];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newCategories.length) return;
    
    // Swap categories
    [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
    
    // Update local state immediately for better UX
    setCategories(newCategories);
    
    // Save new order to backend
    try {
      await axios.put(`${API}/categories/reorder`, {
        category_ids: newCategories.map(c => c.id)
      });
      toast.success('Ordem atualizada');
    } catch (e) {
      // Revert on error
      fetchData();
      toast.error('Erro ao reordenar');
    }
  };

  // Drag and drop for categories
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    
    const newCategories = arrayMove(categories, oldIndex, newIndex);
    setCategories(newCategories);
    
    try {
      await axios.put(`${API}/categories/reorder`, {
        category_ids: newCategories.map(c => c.id)
      });
      toast.success('Ordem atualizada');
    } catch (e) {
      fetchData();
      toast.error('Erro ao reordenar');
    }
  };

  // Reorder products within a category
  const handleMoveProduct = async (categoryId, index, direction) => {
    const categoryProducts = getProductsByCategory(categoryId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= categoryProducts.length) return;
    
    // Swap products
    const newProducts = [...categoryProducts];
    [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];
    
    // Update local state
    const updatedProducts = products.map(p => {
      const idx = newProducts.findIndex(np => np.id === p.id);
      if (idx !== -1) {
        return { ...p, order: idx };
      }
      return p;
    });
    setProducts(updatedProducts);
    
    // Save to backend
    try {
      await axios.put(`${API}/products/reorder`, {
        product_ids: newProducts.map(p => p.id)
      });
      toast.success('Ordem atualizada');
    } catch (e) {
      fetchData();
      toast.error('Erro ao reordenar');
    }
  };

  // Drag and drop for products
  const handleProductDragEnd = async (event, categoryId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const categoryProducts = getProductsByCategory(categoryId);
    const oldIndex = categoryProducts.findIndex(p => p.id === active.id);
    const newIndex = categoryProducts.findIndex(p => p.id === over.id);
    
    const newCategoryProducts = arrayMove(categoryProducts, oldIndex, newIndex);
    
    // Update local state
    const otherProducts = products.filter(p => p.category_id !== categoryId);
    const updatedProducts = [...otherProducts, ...newCategoryProducts.map((p, i) => ({ ...p, order: i }))];
    setProducts(updatedProducts);
    
    try {
      await axios.put(`${API}/products/reorder`, {
        product_ids: newCategoryProducts.map(p => p.id)
      });
      toast.success('Ordem atualizada');
    } catch (e) {
      fetchData();
      toast.error('Erro ao reordenar');
    }
  };

  // Product handlers
  const openProductModal = (product = null, categoryId = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        ...product,
        tags: product.tags?.join(', ') || '',
        promo_price: product.promo_price || '',
        show_in_delivery: product.show_in_delivery !== false,
        option_groups: product.option_groups || []
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '', description: '', category_id: categoryId || '', price: '', promo_price: '',
        sku: '', tags: '', stock_quantity: 100, stock_control: true, is_active: true, show_in_delivery: true,
        variants: [], addons: [], images: [], option_groups: []
      });
    }
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price) || 0,
        promo_price: productForm.promo_price ? parseFloat(productForm.promo_price) : null,
        stock_quantity: parseInt(productForm.stock_quantity) || 0,
        tags: productForm.tags ? productForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        option_groups: productForm.option_groups || []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Produto atualizado');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Produto criado');
      }
      setShowProductModal(false);
      setEditingProduct(null);
      fetchData();
    } catch (e) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDuplicateProduct = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/duplicate`);
      toast.success('Produto duplicado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao duplicar produto');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!confirm('Excluir produto?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Produto removido');
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover produto');
    }
  };

  // Batch entry
  const handleBatchSave = async () => {
    const validRows = batchRows.filter(r => r.name && r.price && r.category);
    if (validRows.length === 0) {
      toast.error('Preencha pelo menos uma linha completa');
      return;
    }

    try {
      // Group by category and create if needed
      const categoryMap = {};
      for (const row of validRows) {
        if (!categoryMap[row.category]) {
          const existing = categories.find(c => c.name.toLowerCase() === row.category.toLowerCase());
          if (existing) {
            categoryMap[row.category] = existing.id;
          } else {
            const res = await axios.post(`${API}/categories`, { name: row.category, is_visible: true });
            categoryMap[row.category] = res.data.id;
          }
        }
      }

      const productsToCreate = validRows.map(row => ({
        name: row.name,
        category_id: categoryMap[row.category],
        price: parseFloat(row.price) || 0,
        stock_quantity: parseInt(row.stock) || 0,
        stock_control: true,
        is_active: true,
        description: '',
        tags: [],
        variants: [],
        addons: [],
        images: []
      }));

      await axios.post(`${API}/products/batch`, { products: productsToCreate });
      toast.success(`${productsToCreate.length} produtos criados`);
      setShowBatchModal(false);
      setBatchRows([{ category: '', name: '', price: '', stock: '' }]);
      fetchData();
    } catch (e) {
      toast.error('Erro ao criar produtos em lote');
    }
  };

  const addBatchRows = (count = 5) => {
    setBatchRows([...batchRows, ...Array(count).fill({ category: '', name: '', price: '', stock: '' })]);
  };

  const updateBatchRow = (index, field, value) => {
    const newRows = [...batchRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setBatchRows(newRows);
  };

  // CSV Import/Export
  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/products/export-csv`);
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cardapio.csv';
      a.click();
      toast.success('CSV exportado');
    } catch (e) {
      toast.error('Erro ao exportar CSV');
    }
  };

  const handleImportCSV = async () => {
    try {
      const res = await axios.post(`${API}/products/import-csv`, csvContent, {
        headers: { 'Content-Type': 'text/plain' }
      });
      toast.success(`${res.data.created} produtos importados`);
      if (res.data.errors?.length > 0) {
        console.error('Import errors:', res.data.errors);
      }
      setShowImportModal(false);
      setCsvContent('');
      fetchData();
    } catch (e) {
      toast.error('Erro ao importar CSV');
    }
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProductForm(prev => ({
        ...prev,
        images: [...prev.images, res.data.url]
      }));
      toast.success('Imagem enviada');
    } catch (e) {
      toast.error('Erro ao enviar imagem');
    }
  };

  const getProductsByCategory = (categoryId) => {
    return products.filter(p => p.category_id === categoryId);
  };

  if (loading) {
    return (
      <DashboardLayout title="Cardápio">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Cardápio">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Header Actions */}
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#0f0f15]">
              <TabsTrigger value="cards" className="data-[state=active]:bg-[#ff6b00]">Cards</TabsTrigger>
              <TabsTrigger value="table" className="data-[state=active]:bg-[#ff6b00]">Planilha</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowBatchModal(true)}
              className="bg-white/10"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Cadastro Rápido
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowImportModal(true)}
              className="bg-white/10"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportCSV}
              className="bg-white/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: '', description: '', order: 0, is_visible: true });
                setShowCategoryModal(true);
              }}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Categoria
            </Button>
          </div>
        </div>

        {/* Collapse/Expand Controls */}
        {activeTab === 'cards' && categories.length > 0 && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={collapseAllCategories}
              className="border-white/20 text-white/70 hover:bg-white/10"
            >
              <ChevronsUpDown className="w-4 h-4 mr-2" />
              Minimizar Todas
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={expandAllCategories}
              className="border-white/20 text-white/70 hover:bg-white/10"
            >
              <ChevronDown className="w-4 h-4 mr-2" />
              Expandir Todas
            </Button>
          </div>
        )}

        {activeTab === 'cards' ? (
          /* Card View with Drag and Drop */
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {categories.map((category, index) => (
                  <SortableCategoryItem
                    key={category.id}
                    category={category}
                    index={index}
                    totalCategories={categories.length}
                    isCollapsed={collapsedCategories[category.id] || false}
                    onToggleCollapse={() => toggleCategoryCollapse(category.id)}
                    productCount={getProductsByCategory(category.id).length}
                    onEdit={() => {
                      setEditingCategory(category);
                      setCategoryForm(category);
                      setShowCategoryModal(true);
                    }}
                    onDelete={() => handleDeleteCategory(category.id)}
                    onMoveUp={() => handleMoveCategory(index, 'up')}
                    onMoveDown={() => handleMoveCategory(index, 'down')}
                  >
                    <div className="px-4 pb-4 pt-2 bg-[#1a1a2e]">
                      <div className="flex gap-2 mb-4">
                        <Button
                          size="sm"
                          onClick={() => openProductModal(null, category.id)}
                          className="bg-[#ff6b00]/20 text-[#ff6b00] hover:bg-[#ff6b00]/30"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Produto
                        </Button>
                        <Badge variant="secondary" className="bg-white/10 self-center">
                          {getProductsByCategory(category.id).length} produtos
                        </Badge>
                      </div>
                      
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleProductDragEnd(event, category.id)}
                      >
                        <SortableContext 
                          items={getProductsByCategory(category.id).map(p => p.id)} 
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {getProductsByCategory(category.id).map((product, productIndex) => (
                              <SortableProductItem
                                key={product.id}
                                product={product}
                                onEdit={openProductModal}
                                onDuplicate={handleDuplicateProduct}
                                onDelete={handleDeleteProduct}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      
                      {getProductsByCategory(category.id).length === 0 && (
                        <div className="text-center py-8 text-white/30">
                          Nenhum produto nesta categoria
                        </div>
                      )}
                    </div>
                  </SortableCategoryItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          /* Table View */
          <Card className="card-adegga overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-white/60">Produto</TableHead>
                  <TableHead className="text-white/60">Categoria</TableHead>
                  <TableHead className="text-white/60">Preço</TableHead>
                  <TableHead className="text-white/60">Estoque</TableHead>
                  <TableHead className="text-white/60">Status</TableHead>
                  <TableHead className="text-white/60 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(product => {
                  const category = categories.find(c => c.id === product.category_id);
                  return (
                    <TableRow key={product.id} className="border-white/5 hover:bg-white/5">
                      <TableCell className="text-white font-medium">{product.name}</TableCell>
                      <TableCell className="text-white/60">{category?.name || '-'}</TableCell>
                      <TableCell className="text-[#ff6b00]">R$ {product.price.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={product.stock_quantity <= product.stock_alert_min ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/60'}>
                          {product.stock_quantity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {product.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openProductModal(product)} className="h-8">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDuplicateProduct(product.id)} className="h-8">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteProduct(product.id)} className="h-8 text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {categories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/40 mb-4">Nenhuma categoria criada</p>
            <Button
              onClick={() => setShowCategoryModal(true)}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Categoria
            </Button>
          </div>
        )}

        {/* Category Modal */}
        <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Nome da categoria"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <Textarea
                placeholder="Descrição (opcional)"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <div className="flex items-center gap-3">
                <Switch
                  checked={categoryForm.is_visible}
                  onCheckedChange={(v) => setCategoryForm({ ...categoryForm, is_visible: v })}
                  data-testid="category-show-in-delivery-switch"
                />
                <span className="text-white/60">Mostrar no Delivery <span className="text-white/30 text-xs">(desligado = só no PDV)</span></span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCategoryModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSaveCategory}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Product Modal */}
        <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
          <DialogContent className="bg-[#13131f] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Nome do produto"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="col-span-2 bg-[#0f0f15] border-white/10 text-white"
                />
                <Select
                  value={productForm.category_id}
                  onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}
                >
                  <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="SKU (opcional)"
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
              </div>
              
              <Textarea
                placeholder="Descrição"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Preço</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Preço Promo</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.promo_price}
                    onChange={(e) => setProductForm({ ...productForm, promo_price: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Estoque</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={productForm.stock_quantity}
                    onChange={(e) => setProductForm({ ...productForm, stock_quantity: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
              </div>
              
              <Input
                placeholder="Tags (separadas por vírgula)"
                value={productForm.tags}
                onChange={(e) => setProductForm({ ...productForm, tags: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              
              {/* Image Upload */}
              <div>
                <label className="text-sm text-white/60 mb-2 block">Imagens</label>
                <div className="flex flex-wrap gap-2">
                  {productForm.images?.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={img} alt="" className="w-16 h-16 rounded object-cover" />
                      <button
                        onClick={() => setProductForm({ ...productForm, images: productForm.images.filter((_, idx) => idx !== i) })}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded bg-[#0f0f15] border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[#ff6b00]">
                    <Upload className="w-5 h-5 text-white/40" />
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
              </div>

              {/* Option Groups - Subcategorias */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm text-white/60">Opções do Produto</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setProductForm({
                      ...productForm,
                      option_groups: [...(productForm.option_groups || []), {
                        id: crypto.randomUUID(),
                        name: '',
                        required: true,
                        max_selections: 1,
                        choices: []
                      }]
                    })}
                    className="text-[#ff6b00] h-7"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Adicionar Opção
                  </Button>
                </div>
                
                {productForm.option_groups?.length > 0 && (
                  <div className="space-y-3">
                    {productForm.option_groups.map((group, groupIndex) => (
                      <div key={group.id} className="p-3 bg-[#0f0f15] rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Nome da opção (ex: Sabor do Gelo)"
                            value={group.name}
                            onChange={(e) => {
                              const newGroups = [...productForm.option_groups];
                              newGroups[groupIndex].name = e.target.value;
                              setProductForm({ ...productForm, option_groups: newGroups });
                            }}
                            className="flex-1 bg-[#13131f] border-white/10 text-white h-8"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newGroups = productForm.option_groups.filter((_, i) => i !== groupIndex);
                              setProductForm({ ...productForm, option_groups: newGroups });
                            }}
                            className="text-red-400 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <label className="flex items-center gap-2 text-white/60">
                            <input
                              type="checkbox"
                              checked={group.required}
                              onChange={(e) => {
                                const newGroups = [...productForm.option_groups];
                                newGroups[groupIndex].required = e.target.checked;
                                setProductForm({ ...productForm, option_groups: newGroups });
                              }}
                              className="rounded"
                            />
                            Obrigatório
                          </label>
                        </div>
                        
                        {/* Choices */}
                        <div className="pl-4 space-y-2">
                          <p className="text-xs text-white/40">Escolhas disponíveis:</p>
                          {group.choices?.map((choice, choiceIndex) => (
                            <div key={choice.id} className="flex items-center gap-2">
                              <Input
                                placeholder="Nome (ex: Limão)"
                                value={choice.name}
                                onChange={(e) => {
                                  const newGroups = [...productForm.option_groups];
                                  newGroups[groupIndex].choices[choiceIndex].name = e.target.value;
                                  setProductForm({ ...productForm, option_groups: newGroups });
                                }}
                                className="flex-1 bg-[#13131f] border-white/10 text-white h-7 text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="+R$"
                                value={choice.price_modifier || ''}
                                onChange={(e) => {
                                  const newGroups = [...productForm.option_groups];
                                  newGroups[groupIndex].choices[choiceIndex].price_modifier = parseFloat(e.target.value) || 0;
                                  setProductForm({ ...productForm, option_groups: newGroups });
                                }}
                                className="w-20 bg-[#13131f] border-white/10 text-white h-7 text-sm"
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  const newGroups = [...productForm.option_groups];
                                  newGroups[groupIndex].choices = newGroups[groupIndex].choices.filter((_, i) => i !== choiceIndex);
                                  setProductForm({ ...productForm, option_groups: newGroups });
                                }}
                                className="text-red-400 h-7 w-7 p-0"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newGroups = [...productForm.option_groups];
                              newGroups[groupIndex].choices = [...(newGroups[groupIndex].choices || []), {
                                id: crypto.randomUUID(),
                                name: '',
                                price_modifier: 0
                              }];
                              setProductForm({ ...productForm, option_groups: newGroups });
                            }}
                            className="text-white/40 h-7 text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Adicionar escolha
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {(!productForm.option_groups || productForm.option_groups.length === 0) && (
                  <p className="text-white/30 text-sm text-center py-4">
                    Adicione opções para produtos como combos (ex: Sabor do Gelo, Tamanho)
                  </p>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.stock_control}
                    onCheckedChange={(v) => setProductForm({ ...productForm, stock_control: v })}
                  />
                  <span className="text-white/60">Controle de estoque</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.is_active}
                    onCheckedChange={(v) => setProductForm({ ...productForm, is_active: v })}
                  />
                  <span className="text-white/60">Ativo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.show_in_delivery !== false}
                    onCheckedChange={(v) => setProductForm({ ...productForm, show_in_delivery: v })}
                    data-testid="product-show-in-delivery-switch"
                  />
                  <span className="text-white/60">Mostrar no Delivery</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowProductModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSaveProduct}>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Batch Entry Modal */}
        <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
          <DialogContent className="bg-[#13131f] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Cadastro Rápido de Produtos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-white/60 text-sm">
                Preencha a tabela abaixo para cadastrar vários produtos de uma vez.
                Categorias novas serão criadas automaticamente.
              </p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/60 w-[25%]">Categoria</TableHead>
                      <TableHead className="text-white/60 w-[35%]">Nome do Produto</TableHead>
                      <TableHead className="text-white/60 w-[20%]">Preço</TableHead>
                      <TableHead className="text-white/60 w-[20%]">Estoque</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchRows.map((row, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="p-1">
                          <Input
                            placeholder="Ex: Cervejas"
                            value={row.category}
                            onChange={(e) => updateBatchRow(i, 'category', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            placeholder="Nome do produto"
                            value={row.name}
                            onChange={(e) => updateBatchRow(i, 'name', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={row.price}
                            onChange={(e) => updateBatchRow(i, 'price', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            placeholder="0"
                            value={row.stock}
                            onChange={(e) => updateBatchRow(i, 'stock', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button variant="ghost" onClick={() => addBatchRows(5)} className="text-[#ff6b00]">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar 5 linhas
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowBatchModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleBatchSave}>
                <Save className="w-4 h-4 mr-2" />
                Salvar Produtos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import CSV Modal */}
        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="bg-[#13131f] border-white/10 w-[95vw] max-w-md mx-auto max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle className="text-white flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#ff6b00]" />
                Importar CSV
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 overflow-y-auto flex-1 pr-2">
              {/* File Upload - Destacado */}
              <div className="p-3 border-2 border-dashed border-[#ff6b00]/50 rounded-lg bg-[#ff6b00]/5 hover:bg-[#ff6b00]/10 transition-colors">
                <label className="block text-center cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-1 text-[#ff6b00]" />
                  <span className="block text-white font-medium text-sm mb-1">Clique para selecionar arquivo</span>
                  <span className="block text-xs text-white/60 mb-2">ou arraste o arquivo CSV aqui</span>
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setCsvContent(event.target.result);
                        };
                        reader.readAsText(file);
                      }
                    }}
                    className="hidden"
                  />
                  <span className="inline-block px-3 py-1.5 bg-[#ff6b00] text-white rounded-lg text-sm font-medium hover:bg-[#ff8533] transition-colors">
                    Escolher Arquivo CSV
                  </span>
                </label>
              </div>
              
              {/* Instructions - Compacto */}
              <div className="p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 font-bold text-xs mb-1">Formato aceito:</p>
                <p className="text-white/80 text-xs mb-1">
                  CSV com cabeçalho contendo:
                </p>
                <ul className="text-white/70 text-xs space-y-0.5 list-disc list-inside">
                  <li><strong>nome</strong> (obrigatório)</li>
                  <li><strong>preco</strong> ou <strong>valor</strong> (obrigatório)</li>
                  <li><strong>categoria</strong> (opcional)</li>
                </ul>
              </div>
              
              {/* Example - Compacto */}
              <div className="p-2 bg-[#0f0f15] rounded-lg">
                <p className="text-white/60 text-xs mb-1">Exemplo:</p>
                <pre className="text-xs text-[#ff6b00] font-mono overflow-x-auto">
{`nome,preco,categoria
Brahma Lata,4.50,Cervejas
Vodka Smirnoff,45.90,Destilados`}
                </pre>
              </div>
              
              {/* Text area for paste */}
              <div>
                <label className="block text-xs text-white/60 mb-1">Ou cole o conteúdo aqui:</label>
                <Textarea
                  placeholder="nome,preco,categoria&#10;Produto 1,10.00,Categoria"
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  className="bg-[#0f0f15] border-white/10 text-white min-h-[80px] font-mono text-xs"
                />
              </div>
              
              {csvContent && (
                <p className="text-green-400 text-xs">
                  ✓ {csvContent.split('\n').filter(l => l.trim()).length - 1} produtos detectados
                </p>
              )}
            </div>
            <DialogFooter className="shrink-0 pt-3 border-t border-white/10 gap-2 flex-col sm:flex-row">
              <Button variant="ghost" className="w-full sm:w-auto" onClick={() => { setShowImportModal(false); setCsvContent(''); }}>
                Cancelar
              </Button>
              <Button 
                className="bg-[#ff6b00] hover:bg-[#ff8533] w-full sm:w-auto" 
                onClick={handleImportCSV}
                disabled={!csvContent.trim()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar Produtos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Menu;
